import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import CouponList from '../components/CouponList';
import type { Customer } from '../types';
import type { PointHistoryItem } from '../types/api';
import { fetchPointHistory } from '../api/auth';
import { routes } from '../config';

type MainTab = 'coupons' | 'points';

interface CouponsPageProps {
  user?: Customer | null;
}

export default function CouponsPage({ user }: CouponsPageProps) {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [activeTab, setActiveTab] = useState<MainTab>('coupons');

  const [history, setHistory] = useState<PointHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async (page: number) => {
    setHistoryLoading(true);
    try {
      const res = await fetchPointHistory(page, 20);
      setHistory(prev => page === 0 ? res.content : [...prev, ...res.content]);
      setHistoryTotalPages(res.totalPages);
    } catch {
      // silently ignore
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'points') loadHistory(0);
  }, [activeTab, loadHistory]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <Box sx={{ flex: 1, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', pb: '30px' }}>
      {/* Page nav */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1.5, position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 5,
        borderBottom: '1px solid #F0F0F0',
      }}>
        <Box
          onClick={() => navigate(routes.home)}
          sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, ml: '-8px', cursor: 'pointer',
            borderRadius: '10px', fontSize: '28px', fontWeight: 300, lineHeight: 1, color: '#1A1A1A',
            '&:active': { backgroundColor: '#F5F5F5' },
          }}
        >‹</Box>
        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1A1A1A' }}>
          {t('home.coupons.title')}
        </Typography>
      </Box>

      {/* Main tabs */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #E5E5E5' }}>
        {(['coupons', 'points'] as MainTab[]).map((tab) => (
          <Box
            key={tab}
            onClick={() => setActiveTab(tab)}
            sx={{
              flex: 1, textAlign: 'center', py: 1.5, cursor: 'pointer', fontSize: '15px', fontWeight: 600,
              color: activeTab === tab ? '#1A1A1A' : '#8A8A8A',
              borderBottom: activeTab === tab ? '2px solid #7BA88A' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            {tab === 'coupons' ? t('home.coupons.title') : t('home.points.title')}
          </Box>
        ))}
      </Box>

      {/* Tab panels */}
      <Box sx={{ flex: 1 }}>
        {activeTab === 'coupons' ? (
          <Box sx={{ px: 2, pt: 2 }}>
            <CouponList />
          </Box>
        ) : (
          <Box>
            {/* Points summary header */}
            <Box sx={{ mt: '18px', px: 2, pb: '24px', textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
              <Typography sx={{ fontSize: '40px', fontWeight: 700, color: '#1A1A1A', mb: '6px', lineHeight: 1.1 }}>
                {(user?.pointsBalance ?? 0).toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#8A8A8A', mb: '16px' }}>
                {t('home.points.available')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#8A8A8A', lineHeight: 1.5 }}>
                {t('home.points.earnDescription')}
              </Typography>
            </Box>

            {/* Point history */}
            <Box sx={{ px: 2, pt: 2, pb: '30px' }}>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A', mb: 1.5 }}>
                {t('home.points.history')}
              </Typography>

              {historyLoading && history.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#7BA88A' }} />
                </Box>
              ) : history.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 4, color: '#9A9A9A', fontSize: '14px' }}>
                  {t('home.points.noHistory')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #F0F0F0' }}>
                  {history.map((item) => {
                    const isEarn = item.action === 'EARN';
                    return (
                      <Box key={item.transactionId} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1.5, backgroundColor: '#fff',
                        borderBottom: '1px solid #F8F8F8',
                        '&:last-child': { borderBottom: 'none' },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28 }}>
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                              {isEarn
                                ? <path d="M12 19V5M5 12l7-7 7 7" stroke="#7BA88A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                : <path d="M12 5v14M19 12l-7 7-7-7" stroke="#E85A22" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                              }
                            </svg>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                              {item.description || item.source}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', color: '#9A9A9A', mt: '2px' }}>
                              {formatDate(item.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{
                          fontSize: '14px', fontWeight: 700,
                          color: isEarn ? '#7BA88A' : '#E85A22',
                          flexShrink: 0, ml: 1,
                        }}>
                          {isEarn ? '+' : '-'}{Math.abs(item.points).toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Load more */}
              {historyPage + 1 < historyTotalPages && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  {historyLoading ? (
                    <CircularProgress size={20} sx={{ color: '#7BA88A' }} />
                  ) : (
                    <Typography
                      onClick={() => { const next = historyPage + 1; setHistoryPage(next); loadHistory(next); }}
                      sx={{ fontSize: '13px', fontWeight: 600, color: '#7BA88A', cursor: 'pointer' }}
                    >
                      {t('common.loadMore')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

    </Box>
  );
}
